import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Building2, Users, FileSignature, Receipt, FileText,
  Folder, Calculator, Bell, Settings, LogOut, Menu, X, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AIAssistant from "@/components/AIAssistant";

const nav = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/biens", label: "Biens", icon: Building2, testid: "nav-biens" },
  { to: "/locataires", label: "Locataires", icon: Users, testid: "nav-locataires" },
  { to: "/baux", label: "Baux", icon: FileSignature, testid: "nav-baux" },
  { to: "/loyers", label: "Loyers", icon: Receipt, testid: "nav-loyers" },
  { to: "/quittances", label: "Quittances", icon: FileText, testid: "nav-quittances" },
  { to: "/documents", label: "Documents", icon: Folder, testid: "nav-documents" },
  { to: "/comptabilite", label: "Comptabilité", icon: Calculator, testid: "nav-comptabilite" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
  { to: "/parametres", label: "Paramètres", icon: Settings, testid: "nav-parametres" },
];

export default function Layout({ children }) {
  const loc = useLocation();
  const nav2 = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const SidebarContent = () => (
    <>
      <div className="px-6 py-5 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-zinc-900 rounded-md flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-bold text-base tracking-tight" style={{fontFamily:'Outfit'}}>GestLocative</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Gestion locative</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5 sidebar-scroll overflow-y-auto">
        {nav.map((it) => {
          const Active = loc.pathname === it.to;
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                Active ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              <span className="font-medium">{it.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="px-3 pb-3 border-t border-zinc-200 pt-3">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs text-zinc-500">Connecté en tant que</div>
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{user?.role}</div>
        </div>
        <Button variant="ghost" size="sm" className="w-full justify-start text-zinc-600" data-testid="logout-btn" onClick={() => { logout(); nav2("/login"); }}>
          <LogOut className="w-4 h-4 mr-2" /> Déconnexion
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-zinc-200 flex-col z-30">
        <SidebarContent />
      </aside>

      {/* mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-zinc-900/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-white border-r border-zinc-200 flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="lg:pl-64 min-h-screen flex flex-col">
        {/* topbar */}
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <button data-testid="mobile-menu-btn" className="lg:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="text-sm text-zinc-500 hidden lg:block">
            <span className="text-overline">{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
          <Button data-testid="ai-open-btn" variant="outline" size="sm" onClick={() => setAiOpen(true)} className="gap-2">
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Assistant IA</span>
          </Button>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          <div className="max-w-screen-2xl mx-auto fade-in-up">{children}</div>
        </main>
      </div>

      <AIAssistant open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
